import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import ProductImageModal from './ProductImageModal';

// Personalized upsell strip. Used on the order guide ("Recommended for you")
// and on the basket page before checkout ("Often ordered with these").
//
// Deliberately self-contained and fail-quiet: it fetches its own data and
// renders nothing at all on error or when the backend has no picks. A
// recommendation strip is an enhancement — it must never break or delay the
// page a customer is trying to order from.
//
// `exclude` is the list of sosIds already in the basket. It's stringified into
// a dependency below rather than passed as an array so a parent re-render with
// an equal-but-new array can't cause a refetch loop.
export default function Recommendations({
  title = 'Recommended for you',
  subtitle = '',
  limit = 6,
  exclude = [],
  onAdded,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [added, setAdded] = useState({});
  const [zoomProduct, setZoomProduct] = useState(null);

  const excludeKey = exclude.join(',');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getRecommendations({ limit, exclude: excludeKey ? excludeKey.split(',') : [] });
        if (!cancelled) setItems(data?.items || []);
      } catch {
        if (!cancelled) setItems([]);   // fail quiet — see note above
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [limit, excludeKey]);

  function handleAdd(product) {
    onAdded?.([{
      itemId: product.sosId || product.id,
      sosId: product.sosId,
      name: product.name,
      description: product.description || '',
      sku: product.sku,
      quantity: 1,
      price: product.price,
      onSale: !!product.onSale,
      originalPrice: product.originalPrice ?? product.price,
      saleLabel: product.saleLabel || null,
    }]);
    setAdded(a => ({ ...a, [product.sosId]: true }));
  }

  if (loading || items.length === 0) return null;

  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <h2 style={styles.title}>{title}</h2>
        {subtitle && <span style={styles.subtitle}>{subtitle}</span>}
      </div>

      <div data-recommendation-strip style={styles.strip}>
        {items.map(product => {
          const isAdded = !!added[product.sosId];
          const rec = product.recommendation || {};
          return (
            <div key={product.sosId} style={styles.card}>
              {product.hasImage && product.sku ? (
                <img
                  src={`/images/products/${product.sku}.jpg`}
                  alt=""
                  loading="lazy"
                  style={styles.thumb}
                  onClick={() => setZoomProduct(product)}
                  onError={e => { e.currentTarget.style.visibility = 'hidden'; }}
                />
              ) : (
                <div style={styles.thumbPlaceholder} aria-hidden="true" />
              )}

              <div style={styles.name}>{product.name}</div>

              <div style={styles.meta}>
                {product.sku && <span style={styles.sku}>{product.sku}</span>}
                {product.brand && <span style={styles.brand}>{product.brand}</span>}
              </div>

              {product.description && (
                <div style={styles.description}>{product.description}</div>
              )}

              <div style={styles.reason}>
                <span style={styles.reasonIcon} aria-hidden="true">{KIND_ICON[rec.kind] || '✦'}</span>
                {rec.reason}
              </div>

              <div style={styles.footer}>
                <span style={styles.price}>
                  {product.onSale && product.originalPrice > product.price ? (
                    <>
                      <s style={styles.wasPrice}>${product.originalPrice.toFixed(2)}</s>{' '}
                      <span style={styles.salePrice}>${product.price.toFixed(2)}</span>
                    </>
                  ) : (
                    product.price ? `$${product.price.toFixed(2)}` : ''
                  )}
                  {product.uom && <span style={styles.uom}> / {product.uom.toLowerCase()}</span>}
                </span>

                <button
                  style={{ ...styles.addBtn, ...(isAdded ? styles.addBtnDone : {}) }}
                  onClick={() => handleAdd(product)}
                  disabled={isAdded}
                >
                  {isAdded ? 'Added ✓' : 'Add'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {zoomProduct && (
        <ProductImageModal product={zoomProduct} onClose={() => setZoomProduct(null)} />
      )}
    </div>
  );
}

const KIND_ICON = {
  reorder: '🔄',
  affinity: '✦',
  popular: '★',
};

const styles = {
  root: { margin: '1.5rem 0 2rem' },
  header: { display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' },
  title: { margin: 0, fontSize: '1.05rem', fontWeight: 600, color: '#333' },
  subtitle: { fontSize: '0.85rem', color: 'rgba(0,0,0,0.45)' },

  // Horizontal scroll rather than a wrapping grid: it keeps the strip to one
  // predictable row height above the order-guide table on every screen size,
  // and it's the gesture customers already use on mobile.
  strip: {
    display: 'flex',
    gap: '0.75rem',
    overflowX: 'auto',
    paddingBottom: '0.5rem',
    scrollSnapType: 'x proximity',
    WebkitOverflowScrolling: 'touch',
  },
  card: {
    flex: '0 0 15rem',
    scrollSnapAlign: 'start',
    display: 'flex',
    flexDirection: 'column',
    border: '1px solid rgba(0,0,0,0.08)',
    borderRadius: '8px',
    padding: '0.85rem',
    background: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  thumb: {
    width: '100%', height: '5.5rem', objectFit: 'contain',
    marginBottom: '0.6rem', cursor: 'zoom-in',
  },
  thumbPlaceholder: { height: '5.5rem', marginBottom: '0.6rem' },

  name: { fontSize: '0.9rem', fontWeight: 600, color: '#222', lineHeight: 1.3, marginBottom: '0.3rem' },
  meta: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.35rem' },
  sku: { fontFamily: 'monospace', fontSize: '0.75rem', color: 'rgba(0,0,0,0.45)' },
  brand: { fontSize: '0.75rem', color: 'rgba(0,0,0,0.45)' },
  description: { fontSize: '0.75rem', color: 'rgba(0,0,0,0.5)', lineHeight: 1.35, marginBottom: '0.45rem' },

  reason: {
    fontSize: '0.75rem', color: '#7a5c00', background: '#fdf6e3',
    border: '1px solid #f2e4bd', borderRadius: '5px',
    padding: '0.3rem 0.45rem', lineHeight: 1.35, marginBottom: '0.6rem',
    display: 'flex', gap: '0.35rem', alignItems: 'flex-start',
  },
  reasonIcon: { flexShrink: 0 },

  // marginTop:auto pins the price/Add row to the bottom so buttons line up
  // across cards whose names and reasons wrap to different heights.
  footer: { marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' },
  price: { fontFamily: 'monospace', fontSize: '0.85rem', color: '#A50B18' },
  wasPrice: { color: 'rgba(0,0,0,0.35)', fontSize: '0.8em' },
  salePrice: { color: '#A50B18' },
  uom: { color: 'rgba(0,0,0,0.4)', fontSize: '0.75rem' },

  addBtn: {
    background: '#A50B18', color: '#fff', border: 'none', borderRadius: '5px',
    padding: '0.4rem 0.85rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
  },
  addBtnDone: { background: '#e8e8e8', color: 'rgba(0,0,0,0.45)', cursor: 'default' },
};
